import {
  PERMISSION_TYPES,
  PERMISSION_MATRIX,
  MODULES,
  MODULE_NAME,
} from '@/constants/permissionMatrix';
import {
  convertLegacyToMatrix,
  convertMatrixToLegacy,
  updateMatrixPermission,
  updateFunctionPermissions,
  updateModulePermissions,
  getMatrixPermission,
  hasAnyPermissions,
  hasModulePermissions,
  createEmptyMatrix,
  validateMatrix,
} from '@/utils/permissionMatrix';

export const PERMISSION_MATRIX_MUTATIONS = {
  SET_MATRIX: 'SET_MATRIX',
  SET_ACTIVE_MODULE: 'SET_ACTIVE_MODULE',
  SET_LOADING: 'SET_LOADING',
  SET_ERRORS: 'SET_ERRORS',
  UPDATE_PERMISSION: 'UPDATE_PERMISSION',
  UPDATE_FUNCTION_PERMISSIONS: 'UPDATE_FUNCTION_PERMISSIONS',
  UPDATE_MODULE_PERMISSIONS: 'UPDATE_MODULE_PERMISSIONS',
  LOAD_PERMISSIONS: 'LOAD_PERMISSIONS',
  RESET_MODULE: 'RESET_MODULE',
};

export const PERMISSION_MATRIX_ACTIONS = {
  INITIALIZE_MATRIX: 'initializeMatrix',
  SET_ACTIVE_MODULE: 'setActiveModule',
  UPDATE_PERMISSION: 'updatePermission',
  UPDATE_FUNCTION_PERMISSIONS: 'updateFunctionPermissions',
  UPDATE_MODULE_PERMISSIONS: 'updateModulePermissions',
  LOAD_PERMISSIONS: 'loadPermissions',
  RESET_MODULE: 'resetModule',
  VALIDATE_MATRIX: 'validateMatrix',
  EXPORT_PERMISSIONS: 'exportPermissions',
  IMPORT_PERMISSIONS: 'importPermissions',
};

const state = {
  matrix: createEmptyMatrix(),

  activeModule: MODULES.CATEGORY,
  loading: false,
  errors: [],

  isValid: true,
  validationErrors: [],
};

const getters = {
  getMatrix: (state) => state.matrix,
  getActiveModule: (state) => state.activeModule,
  getLoading: (state) => state.loading,
  getErrors: (state) => state.errors,
  getValidationErrors: (state) => state.validationErrors,
  getIsValid: (state) => state.isValid,

  getAvailableModules: () => {
    return Object.keys(MODULES).map((key) => {
      const value = MODULES[key];
      return {
        key: MODULES[key],
        value: MODULES[key],
        label: MODULE_NAME[value],
      };
    });
  },

  getCurrentModuleMatrix: (state) => {
    return PERMISSION_MATRIX[state.activeModule] || [];
  },

  getModulePermissions: (state) => {
    return state.matrix[state.activeModule] || {};
  },

  getHasModuleAnyPermissions: (state) => {
    return hasModulePermissions(state.matrix, state.activeModule);
  },

  getLegacyPermissions: (state) => {
    return convertMatrixToLegacy(state.matrix);
  },

  getPermission: (state) => (functionId, permissionType) => {
    return getMatrixPermission(
      state.matrix,
      state.activeModule,
      functionId,
      permissionType,
    );
  },

  getHasFunctionPermissions: (state) => (functionId) => {
    return hasAnyPermissions(state.matrix, state.activeModule, functionId);
  },

  getIsModulePermissionChecked: (state, getters) => (permissionType) => {
    if (permissionType === PERMISSION_TYPES.ALL) {
      return getters.getHasModuleAnyPermissions;
    }

    const currentModuleMatrix = getters.getCurrentModuleMatrix;
    return currentModuleMatrix.every((group) =>
      group.functions.every((func) =>
        getMatrixPermission(
          state.matrix,
          state.activeModule,
          func.id,
          permissionType,
        ),
      ),
    );
  },

  getIsModulePermissionIndeterminate: (state, getters) => (permissionType) => {
    if (permissionType === PERMISSION_TYPES.ALL) {
      return false;
    }

    const currentModuleMatrix = getters.getCurrentModuleMatrix;
    const functions = currentModuleMatrix.flatMap((group) => group.functions);
    const checkedCount = functions.filter((func) =>
      getMatrixPermission(
        state.matrix,
        state.activeModule,
        func.id,
        permissionType,
      ),
    ).length;

    return checkedCount > 0 && checkedCount < functions.length;
  },

  getTotalPermissions: (state) => {
    return convertMatrixToLegacy(state.matrix).length;
  },
};

const mutations = {
  [PERMISSION_MATRIX_MUTATIONS.SET_MATRIX](state, matrix) {
    state.matrix = matrix;
  },

  [PERMISSION_MATRIX_MUTATIONS.SET_ACTIVE_MODULE](state, module) {
    state.activeModule = module;
  },

  [PERMISSION_MATRIX_MUTATIONS.SET_LOADING](state, loading) {
    state.loading = loading;
  },

  [PERMISSION_MATRIX_MUTATIONS.SET_ERRORS](state, errors) {
    state.errors = errors;
  },

  [PERMISSION_MATRIX_MUTATIONS.UPDATE_PERMISSION](
    state,
    { functionId, permissionType, value },
  ) {
    const newMatrix = updateMatrixPermission(
      state.matrix,
      state.activeModule,
      functionId,
      permissionType,
      value,
    );

    state.matrix = Object.assign({}, state.matrix, newMatrix);
  },

  [PERMISSION_MATRIX_MUTATIONS.UPDATE_FUNCTION_PERMISSIONS](
    state,
    { functionId, permissions },
  ) {
    const newMatrix = updateFunctionPermissions(
      state.matrix,
      state.activeModule,
      functionId,
      permissions,
    );

    state.matrix = { ...newMatrix };
  },

  [PERMISSION_MATRIX_MUTATIONS.UPDATE_MODULE_PERMISSIONS](
    state,
    { permissionType, value },
  ) {
    const newMatrix = updateModulePermissions(
      state.matrix,
      state.activeModule,
      permissionType,
      value,
    );

    state.matrix = { ...newMatrix };
  },

  [PERMISSION_MATRIX_MUTATIONS.LOAD_PERMISSIONS](state, permissions) {
    const newMatrix = convertLegacyToMatrix(permissions);
    state.matrix = { ...newMatrix };
    state.errors = [];
  },

  [PERMISSION_MATRIX_MUTATIONS.RESET_MODULE](state) {
    if (state.matrix[state.activeModule]) {
      Object.keys(state.matrix[state.activeModule]).forEach((functionId) => {
        Object.values(PERMISSION_TYPES).forEach((permissionType) => {
          state.matrix[state.activeModule][functionId][permissionType] = false;
        });
      });
    }
  },
};

const actions = {
  [PERMISSION_MATRIX_ACTIONS.INITIALIZE_MATRIX]({ commit }) {
    commit(PERMISSION_MATRIX_MUTATIONS.SET_MATRIX, createEmptyMatrix());
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, []);
  },

  [PERMISSION_MATRIX_ACTIONS.SET_ACTIVE_MODULE]({ commit }, module) {
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ACTIVE_MODULE, module);
  },

  [PERMISSION_MATRIX_ACTIONS.UPDATE_PERMISSION](
    { commit, state },
    { functionId, permissionType, value },
  ) {
    commit(PERMISSION_MATRIX_MUTATIONS.UPDATE_PERMISSION, {
      functionId,
      permissionType,
      value,
    });

    const validation = validateMatrix(state.matrix);
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, validation.errors);
  },

  [PERMISSION_MATRIX_ACTIONS.UPDATE_FUNCTION_PERMISSIONS](
    { commit, state },
    { functionId, permissions },
  ) {
    commit(PERMISSION_MATRIX_MUTATIONS.UPDATE_FUNCTION_PERMISSIONS, {
      functionId,
      permissions,
    });

    const validation = validateMatrix(state.matrix);
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, validation.errors);
  },

  [PERMISSION_MATRIX_ACTIONS.UPDATE_MODULE_PERMISSIONS](
    { commit, state },
    { permissionType, value },
  ) {
    commit(PERMISSION_MATRIX_MUTATIONS.UPDATE_MODULE_PERMISSIONS, {
      permissionType,
      value,
    });

    const validation = validateMatrix(state.matrix);
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, validation.errors);
  },

  [PERMISSION_MATRIX_ACTIONS.LOAD_PERMISSIONS]({ commit }, permissions) {
    commit(PERMISSION_MATRIX_MUTATIONS.SET_LOADING, true);

    try {
      commit(PERMISSION_MATRIX_MUTATIONS.LOAD_PERMISSIONS, permissions);
    } catch (error) {
      commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, [error.message]);
    } finally {
      commit(PERMISSION_MATRIX_MUTATIONS.SET_LOADING, false);
    }
  },

  [PERMISSION_MATRIX_ACTIONS.RESET_MODULE]({ commit }) {
    commit(PERMISSION_MATRIX_MUTATIONS.RESET_MODULE);
  },

  [PERMISSION_MATRIX_ACTIONS.VALIDATE_MATRIX]({ commit, state }) {
    const validation = validateMatrix(state.matrix);
    commit(PERMISSION_MATRIX_MUTATIONS.SET_ERRORS, validation.errors);
    return validation;
  },

  [PERMISSION_MATRIX_ACTIONS.EXPORT_PERMISSIONS]({ state }) {
    return {
      matrix: JSON.parse(JSON.stringify(state.matrix)),
      legacy: convertMatrixToLegacy(state.matrix),
      activeModule: state.activeModule,
    };
  },

  [PERMISSION_MATRIX_ACTIONS.IMPORT_PERMISSIONS]({ commit }, data) {
    if (data.matrix) {
      commit(PERMISSION_MATRIX_MUTATIONS.SET_MATRIX, data.matrix);
    } else if (data.legacy) {
      commit(PERMISSION_MATRIX_MUTATIONS.LOAD_PERMISSIONS, data.legacy);
    }

    if (data.activeModule) {
      commit(PERMISSION_MATRIX_MUTATIONS.SET_ACTIVE_MODULE, data.activeModule);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
